/**
 * Offline command queue (PROJECT_SPEC.md §8.2). A pure state machine over an array of queued
 * commands — no storage, no I/O. The browser persistence layer (IndexedDB/SQLite) belongs to
 * apps/web; this module only decides what a valid queue transition looks like.
 *
 * The client queues COMMANDS, never events (§8 lede): the server decides what happened and when.
 * A rejection is not a failure to retry — it becomes a blocking operator task (OFF-014).
 */
import type { CommandEnvelope, DeviceEvidence } from "../events/envelope.js";

/**
 * AMENDED: the operator changed this command before it synced. It is kept (never deleted — N1) with a
 * local audit entry, and is never sent as a command; its replacement is (OFF-005).
 */
export type QueueState = "QUEUED" | "SENT" | "ACCEPTED" | "REJECTED" | "AMENDED";

/** One thing the operator did to a queued command before sync. Travels with the queue to the server (OFF-005). */
export interface LocalAuditEntry {
  /** Client-minted; the server stores each at most once, so re-sending the audit on a retry is safe. */
  readonly auditId: string;
  readonly action: "AMEND";
  readonly batchId: string;
  readonly clientCommandId: string;
  /** The command that replaced it. */
  readonly replacedByClientCommandId: string;
  readonly before: Record<string, unknown>;
  readonly after: Record<string, unknown>;
  readonly reason: string;
  readonly actorUserId: string;
  /** Untrusted evidence (§8.4): the device's wall clock and monotonic reading at the edit. */
  readonly deviceTime?: string;
  readonly monotonicNs: string;
}

export interface CommandRejection {
  readonly code: string;
  readonly message: string;
}

export interface QueuedCommand {
  readonly clientCommandId: string;
  readonly batchId: string;
  readonly envelope: CommandEnvelope;
  readonly device: DeviceEvidence;
  readonly state: QueueState;
  /** Local append order — the ordering anchor. Never the device clock (§8.4: device_time is untrusted). */
  readonly sequence: number;
  /** Set only in state REJECTED. A blocking task, not a retry signal (OFF-014). */
  readonly rejection?: CommandRejection;
  /** Set on a command that replaces an amended one. */
  readonly amends?: string;
  /** Set on an AMENDED command: the audit entry that explains why it is no longer going to be sent. */
  readonly localAudit?: LocalAuditEntry;
}

export type CommandQueue = readonly QueuedCommand[];

export const emptyQueue: CommandQueue = [];

export type QueueOutcome =
  | { ok: true; queue: CommandQueue }
  | { ok: false; code: "NOT_FOUND" | "WRONG_STATE" | "OUT_OF_ORDER"; message: string };

const find = (queue: CommandQueue, clientCommandId: string) =>
  queue.find((c) => c.clientCommandId === clientCommandId);

/**
 * Appends a command. Idempotent on `clientCommandId` (§8.2 "server-side idempotency… a retried
 * command is a no-op returning the original result") — enqueuing one already present is a no-op,
 * so replaying a batch (OFF-007) never duplicates a queue entry.
 */
export function enqueue(
  queue: CommandQueue,
  envelope: CommandEnvelope,
  device: DeviceEvidence,
): CommandQueue {
  if (find(queue, envelope.clientCommandId)) return queue;
  const sequence = queue.reduce((max, c) => Math.max(max, c.sequence), -1) + 1;
  const entry: QueuedCommand = {
    clientCommandId: envelope.clientCommandId,
    batchId: envelope.batchId,
    envelope,
    device,
    state: "QUEUED",
    sequence,
  };
  return [...queue, entry];
}

/** Commands for one batch replay in queue order; different batches may interleave (§8.2 step 5). */
export function replayOrder(queue: CommandQueue, batchId: string): QueuedCommand[] {
  return queue.filter((c) => c.batchId === batchId).sort((a, b) => a.sequence - b.sequence);
}

/**
 * QUEUED commands eligible to send next: for each batch, only the earliest-sequenced QUEUED
 * command is sendable until it resolves — this is the ordering rule, enforced without a lock.
 * Different batches interleave freely.
 */
export function sendable(queue: CommandQueue): QueuedCommand[] {
  const earliestQueuedByBatch = new Map<string, QueuedCommand>();
  for (const c of queue) {
    if (c.state !== "QUEUED") continue;
    const current = earliestQueuedByBatch.get(c.batchId);
    if (!current || c.sequence < current.sequence) earliestQueuedByBatch.set(c.batchId, c);
  }
  return [...earliestQueuedByBatch.values()].sort((a, b) => a.sequence - b.sequence);
}

function transitionEntry(
  queue: CommandQueue,
  clientCommandId: string,
  from: QueueState,
  update: (c: QueuedCommand) => QueuedCommand,
): QueueOutcome {
  const entry = find(queue, clientCommandId);
  if (!entry)
    return { ok: false, code: "NOT_FOUND", message: `no queued command with id ${clientCommandId}` };
  if (entry.state !== from)
    return {
      ok: false,
      code: "WRONG_STATE",
      message: `command ${clientCommandId} is ${entry.state}, not ${from} — a rejected command is a blocking task, not something to resend (OFF-014)`,
    };
  return { ok: true, queue: queue.map((c) => (c.clientCommandId === clientCommandId ? update(c) : c)) };
}

/** QUEUED → SENT. Refuses out-of-order sends so the per-batch ordering rule can't be bypassed. */
export function markSent(queue: CommandQueue, clientCommandId: string): QueueOutcome {
  const entry = find(queue, clientCommandId);
  if (
    entry &&
    entry.state === "QUEUED" &&
    !sendable(queue).some((c) => c.clientCommandId === clientCommandId)
  )
    return {
      ok: false,
      code: "OUT_OF_ORDER",
      message: `command ${clientCommandId} has an earlier unsent command in batch ${entry.batchId}`,
    };
  return transitionEntry(queue, clientCommandId, "QUEUED", (c) => ({ ...c, state: "SENT" }));
}

/** SENT → ACCEPTED, per the server's authoritative decision (§8.2 step 7). */
export function markAccepted(queue: CommandQueue, clientCommandId: string): QueueOutcome {
  return transitionEntry(queue, clientCommandId, "SENT", (c) => ({ ...c, state: "ACCEPTED" }));
}

/**
 * SENT → REJECTED. Not a failure to retry: the rejection reason is kept and the entry becomes a
 * blocking task for the operator (OFF-014 — rejections are never silently dropped, §8.3).
 */
export function markRejected(
  queue: CommandQueue,
  clientCommandId: string,
  rejection: CommandRejection,
): QueueOutcome {
  return transitionEntry(queue, clientCommandId, "SENT", (c) => ({ ...c, state: "REJECTED", rejection }));
}

/** SENT commands with no server disposition yet — a partial sync must retry these, never drop them (OFF-006). */
export function unresolved(queue: CommandQueue): QueuedCommand[] {
  return queue.filter((c) => c.state === "SENT");
}

/** REJECTED commands: the operator's blocking task list (OFF-014). */
export function blockingTasks(queue: CommandQueue): QueuedCommand[] {
  return queue.filter((c) => c.state === "REJECTED");
}
